import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { releaseApi, releaseNoteApi, wikiArticleApi } from '../utils/api';
import { useAuth } from '../lib/security';
import { Bug, ArrowUp, Plus, ArrowLeft, FileText, X, Link as LinkIcon, CheckCircle2, Clock, ClipboardList } from 'lucide-react';

interface Release {
    id: string;
    version: string;
    plannedReleaseDate: string | null;
    actualReleaseDate: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

interface ReleaseNote {
    id: string;
    releaseId: string;
    jiraKey: string;
    title: string;
    description: string;
    package: string | null;
    /**
     * The customers this note is targeted at, or null when it is general.
     *
     * The API sends this to Binary staff only - a customer reading a note that
     * also went to another customer is not told so - which is why the badge
     * below simply appears or does not, with no check on who is looking.
     */
    companies: string[] | null;
    relatedArticleIds: string[] | null;
    type: number;
    createdAt: string;
    updatedAt: string;
}

const formatDateShort = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const ReleaseNotesHomePage: React.FC = () => {
    const { canWrite } = useAuth();
    const [releases, setReleases] = useState<Release[]>([]);
    const [notes, setNotes] = useState<ReleaseNote[]>([]);
    const [articleTitles, setArticleTitles] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [selectedReleaseId, setSelectedReleaseId] = useState<string | null>(null);
    const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
    const [searchParams] = useSearchParams();

    // /releases/BIF-87
    const { jiraKey: linkedKey } = useParams<{ jiraKey?: string }>();
    const navigate = useNavigate();
    const [linkError, setLinkError] = useState<string | null>(null);

    // Auto-select note from URL query param
    useEffect(() => {
        const noteId = searchParams.get('noteId');
        if (noteId) {
            setSelectedNoteId(noteId);
        }
    }, [searchParams]);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Link modal state
    const [linkSearchQuery, setLinkSearchQuery] = useState('');
    const [availableArticles, setAvailableArticles] = useState<any[]>([]);
    const [linkLoading, setLinkLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const fetchReleases = useCallback(async () => {
        setLoading(true);
        setError(null);
        const result = await releaseApi.getAll();
        if (result.error) {
            setError(result.error);
            setReleases([]);
        } else {
            setReleases(result.data);
        }
        setLoading(false);
    }, []);

    // Validate that a string looks like a GUID
    const isValidGuid = (id: string): boolean => {
        const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        return guidRegex.test(id.trim());
    };

    // Show success message that auto-dismisses after 3 seconds
    const showSuccess = (message: string) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(null), 3000);
    };

    // Safely parse relatedArticleIds — handle both string and array from the API
    const parseRelatedArticleIds = (val: any): string[] => {
        if (Array.isArray(val)) return val;
        if (typeof val === 'string') {
            try {
                const parsed = JSON.parse(val);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        return [];
    };

    const fetchNotes = useCallback(async (releaseId: string) => {
        const result = await releaseNoteApi.getAll(releaseId);
        if (!result.error) {
            // Parse relatedArticleIds for each note (handle both string and array)
            const parsedNotes: ReleaseNote[] = (result.data || []).map((note: any) => ({
                ...note,
                relatedArticleIds: parseRelatedArticleIds(note.relatedArticleIds),
            }));
            setNotes(parsedNotes);
            // Collect all related article IDs and fetch their titles
            const articleIds: string[] = [];
            for (const note of parsedNotes) {
                const ids = note.relatedArticleIds || [];
                for (const id of ids) {
                    const trimmed = id?.trim();
                    if (trimmed && isValidGuid(trimmed) && !articleIds.includes(trimmed)) {
                        articleIds.push(trimmed);
                    }
                }
            }
            if (articleIds.length > 0) {
                const titles: Record<string, string> = {};
                for (const id of articleIds) {
                    const articleResult = await wikiArticleApi.getById(id);
                    if (!articleResult.error && articleResult.data) {
                        const article = articleResult.data as any;
                        titles[id] = article.title || article.name || id;
                    }
                }
                setArticleTitles(prev => ({ ...prev, ...titles }));
            }
        } else {
            setNotes([]);
        }
    }, []);

    useEffect(() => {
        fetchReleases();
    }, [fetchReleases]);

    useEffect(() => {
        if (selectedReleaseId) {
            fetchNotes(selectedReleaseId);
        }
    }, [selectedReleaseId, fetchNotes]);

    // Auto-select the first active or most recent release.
    // Skipped while a link names a note: that note decides which release to
    // open, and choosing one here first would load the wrong release's notes
    // and flash them on screen before the right ones arrived.
    useEffect(() => {
        if (linkedKey) return;
        if (releases.length > 0 && !selectedReleaseId) {
            const active = releases.find(r => r.isActive);
            setSelectedReleaseId(active ? active.id : releases[0].id);
        }
    }, [releases, selectedReleaseId, linkedKey]);

    const selectedNote = notes.find(n => n.id === selectedNoteId) || null;

    /**
     * Turn the key in the URL into a release and a note.
     *
     * Needed because neither is knowable from the key alone, and the note is
     * very likely not in the release the page would otherwise have opened.
     */
    useEffect(() => {
        if (!linkedKey) {
            setLinkError(null);
            return;
        }
        // Already on screen - this runs again whenever the notes reload, and
        // after we navigate here ourselves from a click.
        if (selectedNote && selectedNote.jiraKey?.toUpperCase() === linkedKey.toUpperCase()) {
            setLinkError(null);
            return;
        }

        let cancelled = false;
        (async () => {
            const result = await releaseNoteApi.getByKey(linkedKey);
            if (cancelled) return;

            if (result.error || !result.data) {
                setLinkError(`No release note found for ${linkedKey.toUpperCase()}.`);
            } else if (!result.data.releases?.length) {
                // A real note, just not in a release yet - an issue sitting in
                // the backlog. Worth saying, rather than "not found".
                setLinkError(`${result.data.jiraKey} is not in a release yet.`);
            } else {
                setLinkError(null);
                setSelectedReleaseId(result.data.releases[0].id);
                setSelectedNoteId(result.data.id);
            }
        })();
        return () => { cancelled = true; };
    }, [linkedKey, selectedNote]);

    /**
     * Opening a note puts its key in the address bar, so the link to share is
     * the one already on screen and needs no separate copy button.
     */
    const handleNoteClick = (noteId: string) => {
        setSelectedNoteId(noteId);
        const note = notes.find(n => n.id === noteId);
        if (note?.jiraKey) navigate(`/releases/${encodeURIComponent(note.jiraKey)}`);
    };

    const handleBackToNotes = () => {
        setSelectedNoteId(null);
        if (linkedKey) navigate('/releases');
    };

    const fetchAvailableArticles = async (searchQuery?: string) => {
        setLinkLoading(true);
        const result = await wikiArticleApi.getAll(searchQuery ? { query: searchQuery } : undefined);
        if (!result.error) {
            setAvailableArticles(result.data || []);
        }
        setLinkLoading(false);
    };

    const handleOpenLinkModal = async () => {
        setLinkSearchQuery('');
        await fetchAvailableArticles();
        setShowLinkModal(true);
    };

    const handleLinkSearch = async (query: string) => {
        setLinkSearchQuery(query);
        if (query.trim()) {
            await fetchAvailableArticles(query.trim());
        } else {
            await fetchAvailableArticles();
        }
    };

    const handleAddRelatedArticle = async (articleId: string) => {
        if (!selectedNoteId) return;
        const trimmedId = articleId?.trim();
        if (!trimmedId || !isValidGuid(trimmedId)) {
            setError('Invalid article ID format. Expected a GUID.');
            return;
        }
        const note = notes.find(n => n.id === selectedNoteId);
        if (!note) return;
        const currentIds = Array.isArray(note.relatedArticleIds) ? note.relatedArticleIds : [];
        if (currentIds.includes(trimmedId)) return;
        const updatedIds = [...currentIds, trimmedId];
        const result = await releaseNoteApi.update(selectedNoteId, { relatedArticleIds: updatedIds });
        if (!result.error) {
            await fetchNotes(selectedReleaseId!);
            setSelectedNoteId(selectedNoteId);
            showSuccess('Documentation linked successfully');
        } else {
            setError(result.error);
        }
    };

    const handleRemoveRelatedArticle = async (articleId: string) => {
        if (!selectedNoteId) return;
        const trimmedId = articleId?.trim();
        if (!trimmedId) return;
        const note = notes.find(n => n.id === selectedNoteId);
        if (!note) return;
        const currentIds = Array.isArray(note.relatedArticleIds) ? note.relatedArticleIds : [];
        const updatedIds = currentIds.filter(id => id?.trim() !== trimmedId);
        const result = await releaseNoteApi.update(selectedNoteId, { relatedArticleIds: updatedIds });
        if (!result.error) {
            await fetchNotes(selectedReleaseId!);
            setSelectedNoteId(selectedNoteId);
            showSuccess('Documentation link removed');
        } else {
            setError(result.error);
        }
    };

    const getNoteTypeIcon = (type: number) => {
        switch (type) {
            case 1: return <Bug className="w-4 h-4 text-red-500 shrink-0" />;
            case 2: return <ArrowUp className="w-4 h-4 text-blue-500 shrink-0" />;
            case 3: return <Plus className="w-4 h-4 text-green-500 shrink-0" />;
            default: return null;
        }
    };

    const getReleaseStatus = (release: Release): { label: string; color: string; icon: React.ReactNode } => {
        if (release.isActive && release.actualReleaseDate) {
            return { label: 'Released', color: 'bg-green-100 text-green-700', icon: <CheckCircle2 className="w-3.5 h-3.5" /> };
        }
        if (release.isActive) {
            return { label: 'Planned', color: 'bg-amber-100 text-amber-700', icon: <Clock className="w-3.5 h-3.5" /> };
        }
        return { label: 'Draft', color: 'bg-slate-100 text-slate-600', icon: <ClipboardList className="w-3.5 h-3.5" /> };
    };

    const getReleaseDateInfo = (release: Release): { label: string; date: string } => {
        if (release.isActive && release.actualReleaseDate) {
            return { label: 'Released', date: formatDateShort(release.actualReleaseDate) };
        }
        if (release.isActive && release.plannedReleaseDate) {
            return { label: 'Planned', date: formatDateShort(release.plannedReleaseDate) };
        }
        return { label: 'Created', date: formatDateShort(release.createdAt) };
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="bx-wrap py-4 fade-in">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Release Notes</h1>
                    <p className="text-gray-500 mt-2">Software release notes and linked documentation</p>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="mx-auto max-w-7xl px-4">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {error}
                    </div>
                </div>
            )}

            {/* A link that named a note we cannot show. Amber, not red: the
                page itself is fine and still usable, it is the link that went
                nowhere. */}
            {linkError && (
                <div className="mx-auto max-w-7xl px-4">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center justify-between gap-4">
                        <span>{linkError}</span>
                        <button
                            onClick={() => { setLinkError(null); navigate('/releases'); }}
                            className="shrink-0 text-amber-800 hover:text-amber-900 underline"
                        >
                            Show all releases
                        </button>
                    </div>
                </div>
            )}

            {/* Success */}
            {successMessage && (
                <div className="mx-auto max-w-7xl px-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                        {successMessage}
                    </div>
                </div>
            )}

            <div>
                <div className="flex gap-8">
                    {/* Left Sidebar - Release List.
                        Same treatment as the documentation rail in WikiDocsNav: the
                        card sticks while the notes scroll past it, and the list gets
                        its own scroll once the versions outgrow the viewport, so a
                        long release history never pushes the page down. */}
                    <div className="w-72 flex-shrink-0 no-print">
                        <div className="bx-rail">
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Versions</h2>
                                </div>
                                <div className="bx-rail-scroll">
                                {releases.map((release) => {
                                    const status = getReleaseStatus(release);
                                    const dateInfo = getReleaseDateInfo(release);
                                    const isSelected = selectedReleaseId === release.id;
                                    return (
                                        <button
                                            key={release.id}
                                            onClick={() => setSelectedReleaseId(release.id)}
                                            className={`w-full text-left px-4 py-4 transition-all ${
                                                isSelected
                                                    ? 'bg-blue-50 border-l-4 border-l-blue-600'
                                                    : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-base font-semibold text-gray-900">
                                                    {release.version}
                                                </span>
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                                                    <span>{status.icon}</span>
                                                    {status.label}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-500">
                                                {dateInfo.label}: {dateInfo.date}
                                            </p>
                                        </button>
                                    );
                                })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Panel - Release Notes */}
                    <div className="flex-1 min-w-0">
                        {selectedNoteId && selectedNote ? (
                            /* Detail View */
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <h3 className="text-base font-semibold text-gray-900 truncate">
                                        {selectedNote.jiraKey} · {selectedNote.title}
                                      </h3>
                                    </div>
                                    <button
                                        onClick={handleBackToNotes}
                                        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                        Back to Notes
                                    </button>
                                </div>
                                <div className="p-5">
                                  <div className="flex justify-between">
                                    <div className="text-xs text-gray-400 mb-4">
                                        Created: {formatDateShort(selectedNote.createdAt)} · Updated: {formatDateShort(selectedNote.updatedAt)}
                                    </div>
                                    <div className="flex items-start gap-3 mb-4 flex-wrap">
                                        {selectedNote.package && (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 shrink-0">
                                            {selectedNote.package}
                                          </span>
                                        )}
                                        {selectedNote.companies?.map((company) => (
                                          <span
                                            key={company}
                                            title="Only this customer sees this note"
                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 shrink-0"
                                          >
                                            {company}
                                          </span>
                                        ))}
                                    </div>
                                    </div>
                                    <div className="prose prose-sm max-w-none mb-6">
                                        <div dangerouslySetInnerHTML={{ __html: selectedNote.description }} />
                                    </div>
                                    {Array.isArray(selectedNote.relatedArticleIds) && selectedNote.relatedArticleIds.length > 0 && (
                                        <div className="mb-6">
                                            <h3 className="text-sm font-semibold text-gray-700 mb-2">Linked Documentation</h3>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedNote.relatedArticleIds.map((articleId) => (
                                                    <span key={articleId} className="inline-flex items-center gap-1 px-1.5 py-1 bg-blue-50 border border-blue-200 rounded-lg">
                                                        <Link
                                                            to={`/wiki/article/${articleId}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className=" text-xs font-medium text-blue-600 hover:underline"
                                                        >
                                                          <div className="flex gap-1 px-1">
                                                            <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                                                            {articleTitles[articleId] || articleId}
                                                            </div>
                                                        </Link>
                                                        {canWrite('releaseNotes') && (
                                                            <button
                                                                onClick={() => handleRemoveRelatedArticle(articleId)}
                                                                className="ml-1 text-blue-400 hover:text-red-500 transition-colors"
                                                                title="Remove link"
                                                            >
                                                                <X className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {canWrite('releaseNotes') && (
                                        <div className="pt-4 border-t border-gray-100">
                                            <button
                                                onClick={handleOpenLinkModal}
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                                            >
                                                <LinkIcon className="w-4 h-4" />
                                                Link Documentation
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            /* Notes List View */
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                {notes.length === 0 ? (
                                    <div className="p-5 text-center text-gray-500 text-sm">No items in this release.</div>
                                ) : (
                                    <div className="grid">
                                        {notes.map((note) => {
                                            const relatedArticles = Array.isArray(note.relatedArticleIds) ? note.relatedArticleIds : [];
                                            return (
                                                <button
                                                    key={note.id}
                                                    onClick={() => handleNoteClick(note.id)}
                                                    className={`bg-white border border-gray-200 border-r-4 ${({1: 'border-r-red-500', 2: 'border-r-blue-500', 3: 'border-r-green-500'}[note.type] || 'border-r-transparent')} p-5 hover:shadow-md transition-shadow group cursor-pointer text-left w-full`}
                                                >
                                                    <div className="flex items-start justify-between gap-3 mb-2">
                                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                                            {getNoteTypeIcon(note.type)}
                                                            {note.jiraKey && (
                                                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-mono font-medium bg-gray-100 text-gray-600 shrink-0">
                                                                  {note.jiraKey}
                                                              </span>
                                                            )}
                                                            <h3 className="text-base font-semibold text-gray-900 truncate">
                                                                {note.title}
                                                            </h3>
                                                        </div>
                                                        {note.package && (
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 shrink-0">
                                                                {note.package}
                                                            </span>
                                                        )}
                                                        {note.companies?.map((company) => (
                                                            <span
                                                                key={company}
                                                                title="Only this customer sees this note"
                                                                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-teal-50 text-teal-700 shrink-0"
                                                            >
                                                                {company}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <p className="text-left text-sm text-gray-600 leading-relaxed line-clamp-2 mb-3">                                                    
                                                        <div dangerouslySetInnerHTML={{ __html: note.description }} />
                                                    </p>
                                                    {relatedArticles.length > 0 && (
                                                        <div className="flex items-center gap-2 flex-wrap mt-2">
                                                            {relatedArticles.map((articleId) => (
                                                                <span key={articleId} className="inline-flex items-center gap-1 px-1.5 py-1 bg-blue-50 border border-blue-200 rounded-lg">
                                                                    <Link
                                                                        to={`/wiki/article/${articleId}`}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className=" text-xs font-medium text-blue-600 hover:underline"
                                                                    >
                                                                      <div className="flex gap-1 px-1">
                                                                        <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                                                                        {articleTitles[articleId] || articleId}
                                                                        </div>
                                                                    </Link>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Link Documentation Modal */}
            {showLinkModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 fade-in">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900">Link Documentation</h2>
                            <button
                                onClick={() => setShowLinkModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="mb-4">
                            <input
                                type="text"
                                value={linkSearchQuery}
                                onChange={(e) => handleLinkSearch(e.target.value)}
                                placeholder="Search wiki articles..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                            />
                        </div>
                        <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                            {linkLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="spinner"></div>
                                </div>
                            ) : (
                                availableArticles.map((article) => {
                                    const articleId = (article.id || article._id || '').trim();
                                    const articleTitle = article.title || article.name || 'Untitled';
                                    const isLinked = !!(selectedNote && Array.isArray(selectedNote.relatedArticleIds) && selectedNote.relatedArticleIds.some(id => id?.trim() === articleId));
                                    return (
                                        <button
                                            key={articleId}
                                            onClick={() => handleAddRelatedArticle(articleId)}
                                            disabled={isLinked}
                                            className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors flex items-center justify-between gap-3 ${
                                                isLinked
                                                    ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                                                    : 'text-gray-700'
                                            }`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium text-sm truncate">{articleTitle}</div>
                                                <div className="text-xs text-gray-400 font-mono">{articleId}</div>
                                            </div>
                                            {isLinked ? (
                                                <span className="text-xs text-green-600 font-medium shrink-0">Linked</span>
                                            ) : (
                                                <Plus className="w-4 h-4 text-blue-500 shrink-0" />
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                        {availableArticles.length === 0 && !linkLoading && (
                            <p className="text-sm text-gray-400 text-center py-4">No articles found</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReleaseNotesHomePage;
